import { DangerousGoodsInformation } from "@/model/transportorder/transportOrderDangerousGoods"
import { LengthMeasurement, VolumeMeasurement, WeightMeasurement } from "./measurement"
import { isEqualIgnoringUndefined } from "@/helper/equalityHelper"
import omit from "lodash/omit"

export interface TransportOrder {
    id: string
    orderType: TransportOrderType
    created_at: string
    external_order_created_date?: string
    external_bundle_id?: string
    version?: number
    customer_data?: CustomerData
    buyer?: Buyer
    status?: TransportOrderStatus
    external_order_number?: string
    external_identification?: string
    external_identification_type?: string
    _embedded?: {
        shipments: Shipment[]
    }
}

export enum TransportOrderType {
    MATERIAL = "MATERIAL",
    EMPTIES = "EMPTIES",
}

export enum TransportOrderStatus {
    UNPLANNED = "UNPLANNED",
    CANCELLED = "CANCELLED",
}

export enum ShipmentStatus {
    UNPLANNED = "UNPLANNED",
    CANCELLED = "CANCELLED",
}

export const getNonCancelledShipments = (transportOrder: TransportOrder): Shipment[] =>
    (transportOrder._embedded?.shipments ?? []).filter(shipment => shipment.status !== ShipmentStatus.CANCELLED)

export interface Shipment {
    id: string
    transport_order_id: string
    brand?: string
    external_consignment_reference_number?: string
    version: number
    last_modified_date?: string
    createdDate: string
    freight: Freight
    processIndicator: "FTL" | "LTL" | "KEP"
    external_identification?: string
    external_identification_type?: string
    additional_information?: string
    buyer?: Buyer
    supplier: Supplier
    freightForwarder: FreightForwarder
    startOfTransportChain: StartOfTransportChain
    endOfTransportChain: EndOfTransportChain
    currentTransportLeg: CurrentTransportLeg
    status?: ShipmentStatus
    confirmationDraft: Confirmation
    isConfirmed: boolean
    proposedVehicleType?: VehicleType
    requiresTwoDrivers?: boolean
    priority: ShipmentPriority
    isStartOfTransportChain: boolean
    isEndOfTransportChain: boolean
}

export interface Supplier {
    name?: string
    address?: Address
    externalIdentifiers: PartyExternalIdentifiers
}

export interface PartyExternalIdentifiers {
    duns?: string
    plantCode?: string
    supplierCode?: string
}

export interface FreightForwarder {
    name: string
    duns: string
    accountId?: string
    supplierCode: string
    address?: Address
}

export enum ShipmentPriority {
    REGULAR = "REGULAR",
    URGENT = "URGENT",
}

export type VehicleType = "PASSENGER_CAR" | "VAN" | "LIGHT_TRUCK" | "MEDIUM_TRUCK"

export type FreightDiscriminator = "BULK_DRY" | "BULK_LIQUID" | CargoFreightType

export interface Freight {
    type: FreightDiscriminator
    name?: string
    gross_weight?: WeightMeasurement
    gross_volume?: VolumeMeasurement
}

export type BulkFreightType = "BULK_DRY" | "BULK_LIQUID"

export interface BulkFreight extends Freight {
    type: BulkFreightType
    article_id?: string
    dangerous_goods_information?: DangerousGoodsInformation
}

export type CargoFreightType = "CARGO_MATERIAL" | "CARGO_EMPTIES"

export interface CargoFreight extends Freight {
    type: CargoFreightType
    loading_meters?: LengthMeasurement
    handling_units: HandlingUnit[]
}

export const isCargoFreight = (freight: Freight): freight is CargoFreight =>
    freight.type === "CARGO_MATERIAL" || freight.type === "CARGO_EMPTIES"

export type Packable = HandlingUnit | Article

export interface HandlingUnit {
    type: "HANDLING_UNIT"
    identifier?: string
    additional_information?: string
    quantity?: number
    packaging_material_id?: string
    dimension?: HandlingUnitDimension
    weight?: WeightMeasurement
    stacking_factor?: number
    content: Packable[]
    _frontendKey: string
}

export interface HandlingUnitDimension {
    length?: LengthMeasurement
    width?: LengthMeasurement
    height?: LengthMeasurement
}

export interface CountryOfOrigin {
    country_code?: string
    dutiable_goods: boolean
}

export interface Article {
    type: "ARTICLE"
    article_id?: string
    name?: string
    quantity?: number
    dangerous_goods_information?: DangerousGoodsInformation
    net_explosive_mass?: WeightMeasurement
    internal_place_of_destination?: string
    country_of_origin?: CountryOfOrigin
    _frontendKey: string
}

export interface CustomerData {
    name: string
    contact?: string
    address?: Address
    telephone?: string
    email?: string
}

export interface Buyer {
    name: string
    address?: Address
}

export interface TimeWindow {
    time_from?: string
    time_to: string
}

export interface Address {
    street: string
    /**
     * @deprecated: is always undefined.
     */
    house_number?: string
    postal_code: string
    city: string
    country: string
}

export interface Contact {
    name?: string
    email?: string
    phone?: string
}

export interface ShippingParty {
    name?: string
    address?: Address
    contact?: Contact
    externalIdentifiers: PartyExternalIdentifiers
}

export interface CurrentTransportLegParty {
    name?: string
    address?: Address
    externalIdentifiers: PartyExternalIdentifiers
}

export interface StartOfTransportChain {
    consignor: ShippingParty
    pickupRequestedAt: TimeWindow
    accessCode?: string
    placeOfLoading?: string
}

export interface EndOfTransportChain {
    consignee: ShippingParty
    deliveryEstimatedAt?: TimeWindow
    accessCode?: string
    placeOfDischarge?: string
}

export interface CurrentTransportLeg {
    placeOfDeparture: CurrentTransportLegParty
    departureRequestedAt?: TimeWindow
    placeOfArrival: CurrentTransportLegParty
    arrivalEstimatedAt?: TimeWindow
}

export interface Confirmation {
    updatedAt?: string
    transportChainReference?: string
    asset?: Asset
    accessCodeAtLoading?: string
    accessCodeAtUnloading?: string
    loadingTimeWindow?: TimeWindow
    unloadingTimeWindow?: TimeWindow
    phoneNumber?: string
    vehicleType?: VehicleType
    twoDrivers?: boolean
    loadId?: string
    crossDockId?: string
}

export interface Asset {
    id?: string
    licensePlate?: string
    countryCode?: string
}

export interface ShipmentConfirmation {
    transportOrderId: string
    shipmentId: string
    shipmentStatus?: ShipmentStatus
    version: number
    confirmation: Confirmation
}

export const applyConfirmationToAllShipments = (confirmation: Confirmation, transportOrder: TransportOrder): ShipmentConfirmation[] => {
    const allConfirmations = transportOrder._embedded?.shipments.map(shipment => ({
        transportOrderId: transportOrder.id,
        shipmentId: shipment.id,
        shipmentStatus: shipment.status,
        version: shipment.version,
        confirmation: confirmation,
    })) ?? []
    return confirmationsForNonCancelled(allConfirmations)
}
export const confirmationsForNonCancelled = (confirmations: ShipmentConfirmation[]): ShipmentConfirmation[] =>
    confirmations.filter(it => it.shipmentStatus !== ShipmentStatus.CANCELLED)
export const areConfirmationsEqual = (one: Confirmation, another: Confirmation): boolean => {
    return isEqualIgnoringUndefined(
        omit(one, ["updatedAt"]),
        omit(another, ["updatedAt"])
    )
}
export const areShipmentConfirmationsEqual = (one: ShipmentConfirmation[], another: ShipmentConfirmation[]): boolean => {
    return (
        one.length === another.length &&
        one.every(oneConfirmation => {
            const other = another.find(anotherConfirmation => anotherConfirmation.shipmentId === oneConfirmation.shipmentId)

            return other ? areConfirmationsEqual(oneConfirmation.confirmation, other.confirmation) : false
        }) &&
        another.every(oneConfirmation => {
            const other = one.find(anotherConfirmation => anotherConfirmation.shipmentId === oneConfirmation.shipmentId)

            return other ? areConfirmationsEqual(oneConfirmation.confirmation, other.confirmation) : false
        })
    )
}
